import { useState, useEffect, useRef, useCallback, UIEvent, FC } from "react";
import { Params, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Modal, IconButton } from "@mui/material";
import SvgIcon from "@mui/material/SvgIcon";

import "./style.scss";
import { ReactComponent as ArrowExpandIcon } from "../../assets/arrowExpandIcon.svg";
import { ChatEvents } from "../../common/enums/chat-events.enum";
import { SourceType } from "../../common/enums/source-type.enum";
import { User } from "../../common/interfaces/comps/user.interface";
import { Message } from "../../common/interfaces/chats/message.interface";
import { SummaryProps } from "../../common/interfaces/chats/summary.interface";
import { FeedbackProps } from "../../common/interfaces/chats/feedback.interface";
import { ToastContainer } from "../../components/toast/toast.comp";
import { ThemeContainer } from "../../common/containers/theme/theme.container";
import { FirebaseAuthContainer } from "../../common/containers/auth/firebase-auth-container.util";
import { TivaDataWebSocketContainer } from "../../common/containers/tiva-data/tiva-data-web-socket.container";
import TivaDataModalView from "../tiva-data/tiva-data";
import {
    ChatBubble,
    ChatBubbleType,
    Feedback
} from "../../components/chat-bubble/chat-bubble.comp";
import { LoaderView } from "../../components/loader/loader.comp";
import {
    ChatMessageReview,
    ChatMessageReviewBackdrop
} from "../../components/chat-message-review/chat-message-review.comp";
import {
    listenAnswers,
    addQuestion,
    fetchChatHistory,
    summarizeVideo,
    feedback
} from "../../api/chats/endpoints-referrers";
import { getPresignedUrl } from "../../api/sources/endpoints-referrers";
import { isMobileDevice } from "../../common/utilities/platform/platform.util";
import { useWindowDimensions } from "../../common/utilities/window-dimensions/window-dimensions.util";
import {
    stringGetter,
    objGetter
} from "../../common/utilities/localstorage-dealer/localstorage-getters.util";
import {
    setDataToLocalStorage,
    removeDataToLocalStorage
} from "../../common/utilities/localstorage-dealer/localstorage-setter.util";

interface SseMessage {
    id: number;
    error: any;
    answer: string;
    isStreaming: boolean;
    isWaiting: boolean;
    sessionId: string;
    answerId?: string;
    createdAt?: string;
    metadata?: any;
    question?: string;
    isRetry?: boolean;
    payload?: any;
}

export interface ChatMessage {
    id?: number;
    uid?: string;
    answerId?: string;
    text: string;
    data?: any;
    isLoading: boolean;
    type: ChatBubbleType;
    isExpanded?: boolean;
    metadata: any;
    feedback?: FeedbackProps;
}

export interface ChatMessagePair {
    question?: ChatMessage;
    answer?: ChatMessage;
}

export const ChatMessages = ({
    newMessage,
    setNewMessage,
    placeholder
}: {
    newMessage: string | null;
    setNewMessage: (arg1: string | null) => void;
    placeholder: FC<React.SVGProps<SVGSVGElement>>;
}) => {
    interface QuestionEvent {
        chatId: string;
        message: ChatMessage;
    }

    const answerTime = 60000;
    const activeQuestionId = "activeQuestionId";
    const activeAnswerId = "activeAnswerId";
    const headerHeight = 56;
    const footerHeight = 80;
    const messageSpace = 12;
    const messagePadding = 16;
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { chatId } = useParams<Params<string>>();
    const { user, currentAccessToken }: { user: User | null; currentAccessToken?: string } =
        FirebaseAuthContainer.useContainer();
    const { isAuth } = TivaDataWebSocketContainer.useContainer();
    const { devMode } = ThemeContainer.useContainer();
    const { showToast } = ToastContainer.useContainer();
    const { width } = useWindowDimensions();
    const chatScrollRef = useRef<HTMLDivElement>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const reviewRef = useRef<HTMLDivElement>(null);
    const [messages, setMessages] = useState<ChatMessagePair[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [activeMessage, setActiveMessage] = useState<number>();
    const [reviewScroll, setReviewScroll] = useState<boolean>(false);
    const [reviewScrollTop, setReviewScrollTop] = useState<number>(0);
    const [reviewHeight, setReviewHeight] = useState<number>(0);
    const [isScrolledDown, setScrolledDown] = useState<boolean>(true);
    const availableScrollDownType = useRef<ScrollBehavior | null>(null);
    const [tivaDataResultOpen, setTivaDataResultOpen] = useState<boolean>(false);
    // const [tivaDataPayload, setTivaDataPayload] = useState<any>();

    useEffect(() => {
        // searchParams.append("tivaDataOpen", tivaDataResultOpen.toString());
        // searchParams.set("tivaDataOpen", tivaDataResultOpen.toString());
        // history.replaceState(null, "", `?tivaDataOpen=${tivaDataResultOpen}`);

        setTivaDataResultOpen(!!searchParams.get("tivaDataPayload"));
    }, [searchParams]);

    const isActiveMessage = (message?: ChatMessage) => {
        return !!message?.id && message.id === activeMessage;
    };

    const scrollToBottom = (type: ScrollBehavior) => {
        messagesEndRef.current?.scrollIntoView({ behavior: type });
    };

    const scrollToReviewMessage = () => {
        reviewRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    };

    const typingMessage = (): ChatMessage => {
        return {
            text: "typing",
            isLoading: true,
            type: ChatBubbleType.typing,
            metadata: null
        };
    };

    const approveSentMessage = useCallback((uid: string, id: number) => {
        setMessages(prevMessages =>
            prevMessages
                .map(item =>
                    item.answer?.type === ChatBubbleType.typing
                        ? { ...item, answer: undefined }
                        : item
                )
                .map(item =>
                    item.question?.uid === uid
                        ? {
                              question: { ...item.question, id: id, isLoading: false },
                              answer: typingMessage()
                          }
                        : item
                )
        );
    }, []);

    const setHistoryMessages = useCallback((items: Message[]) => {
        setMessages(
            items
                .filter(item => item.question || item.answer)
                .map((item, index) => {
                    const question = item.question
                        ? {
                              id: item.id,
                              text: item.question,
                              isLoading: false,
                              type: ChatBubbleType.question,
                              metadata: null
                          }
                        : undefined;
                    const answer =
                        item.answer || item.payload
                            ? {
                                  id: item.id,
                                  text: item.answer
                                      ? item.answer
                                      : item.error
                                        ? (item.error?.message ??
                                          "The server had an error. Try again later")
                                        : item.payload?.error
                                          ? `The server had an error. Try again later (Tiva Data):\n- type: **error**\n- message: **${item.payload?.error?.message ?? "none"}**`
                                          : item.payload?.result?.payload === "Something went wrong"
                                            ? "The server had an error. Try again later (Tiva Data):\n- type: **text**\n- message: **Something went wrong**"
                                            : item.payload
                                              ? `Showing “${item.question}"`
                                              : "The server had an error. Try again later",
                                  data: item.payload?.result,
                                  isLoading: false,
                                  type:
                                      item.error ||
                                      item.payload?.error ||
                                      item.payload?.result?.payload === "Something went wrong"
                                          ? ChatBubbleType.error
                                          : item.payload
                                            ? ChatBubbleType.tivaData
                                            : item.question
                                              ? ChatBubbleType.answer
                                              : ChatBubbleType.summary,
                                  metadata: item.metadata
                              }
                            : index === items.length - 1 &&
                                new Date().getTime() - new Date(item.createdAt).getTime() <
                                    answerTime
                              ? typingMessage()
                              : undefined;

                    return { question, answer };
                })
                .reverse()
        );
    }, []);

    useEffect(() => {
        if (availableScrollDownType.current) {
            scrollToBottom(availableScrollDownType.current);
            availableScrollDownType.current = null;
        }
    }, [messages]);

    useEffect(() => {
        const handleStorageEventListener = (event: WindowEventMap["storage"]) => {
            switch (event.key) {
                case ChatEvents.QUESTION: {
                    const chatEvent = objGetter(ChatEvents.QUESTION) as QuestionEvent;
                    if (chatEvent?.chatId === chatId) {
                        availableScrollDownType.current = "smooth";
                        setMessages(prevMessages => [
                            ...prevMessages.map(item =>
                                item.answer?.type === ChatBubbleType.typing
                                    ? { ...item, answer: undefined }
                                    : item
                            ),
                            {
                                question: { ...chatEvent.message, isLoading: false },
                                answer: typingMessage()
                            }
                        ]);
                    }
                    break;
                }
            }
        };

        window.addEventListener("storage", handleStorageEventListener);

        return () => {
            window.removeEventListener("storage", handleStorageEventListener);
        };
    }, [chatId]);

    useEffect(() => {
        if (newMessage && user && chatId) {
            const messageUid = crypto.randomUUID();
            const message = {
                uid: messageUid,
                text: newMessage,
                isLoading: true,
                type: ChatBubbleType.question,
                metadata: null
            };
            availableScrollDownType.current = "smooth";
            setMessages(prevMessages => [...prevMessages, { question: message }]);

            setNewMessage(null);

            addQuestion(user.uid, chatId, { uuid: messageUid, question: newMessage }).then(
                response => {
                    approveSentMessage(messageUid, response.data.messageId);
                    setDataToLocalStorage(ChatEvents.QUESTION, {
                        chatId: chatId,
                        message: message
                    });
                }
            );
        }
    }, [newMessage, user, chatId, setNewMessage, approveSentMessage]);

    useEffect(() => {
        if (!user || !chatId) return;

        if (!location.state?.isNew || !stringGetter("createNewChatKey")) {
            setIsLoading(true);
            fetchChatHistory(user.uid, chatId).then(response => {
                availableScrollDownType.current = "auto";
                setHistoryMessages(response.data.items);
                setIsLoading(false);
            });
        } else {
            removeDataToLocalStorage("createNewChatKey");
        }
        closeReview();
    }, [user, chatId, location.state, setHistoryMessages]);

    const handleAnswer = useCallback((message: SseMessage) => {
        if (!message.id && !message.error) return;

        if (!message.isStreaming) {
            console.log("New message", message);
        }

        setMessages(prevMessages => {
            const list = prevMessages.map(item =>
                item.answer?.type === ChatBubbleType.typing /// TODO can/will be remove
                    ? { ...item, answer: undefined }
                    : item
            );
            const currentMessage = prevMessages.find(
                item =>
                    item.answer?.id === message.id &&
                    (item.answer?.type === ChatBubbleType.answer ||
                        item.answer?.type === ChatBubbleType.summary ||
                        item.answer?.type === ChatBubbleType.error)
            );
            if (currentMessage) {
                return list.map(item => {
                    if (item.answer?.id === message.id) {
                        const text =
                            message.isStreaming &&
                            !message.isRetry &&
                            item.answer.answerId === message.answerId
                                ? item.answer.text + message.answer
                                : message.answer;
                        return !item.answer.isLoading
                            ? item
                            : message.isWaiting
                              ? {
                                    ...item,
                                    answer: {
                                        ...item.answer,
                                        isLoading: message.isStreaming,
                                        answerId: message.answerId,
                                        text
                                    }
                                }
                              : {
                                    ...item,
                                    answer: {
                                        ...item.answer,
                                        text,
                                        isLoading: false,
                                        answerId: message.answerId,
                                        type: message.error
                                            ? ChatBubbleType.error
                                            : message.question
                                              ? ChatBubbleType.answer
                                              : ChatBubbleType.summary,
                                        metadata: message.metadata
                                    }
                                };
                    }
                    return item;
                });
            } else if (!!message.answer || !!message.payload || !!message.error) {
                const answer = {
                    id: message.id,
                    answerId: message.answerId,
                    text: message.answer
                        ? message.answer
                        : message.error
                          ? (message.error?.message ?? "The server had an error. Try again later")
                          : message.payload?.error
                            ? `The server had an error. Try again later (Tiva Data):\n- type: **error**\n- message: **${message.payload?.error?.message ?? "none"}**`
                            : message.payload?.result?.payload === "Something went wrong"
                              ? "The server had an error. Try again later (Tiva Data):\n- type: **text**\n- message: **Something went wrong**"
                              : message.payload
                                ? `Showing “${message.question}"`
                                : "The server had an error. Try again later",
                    data: message.payload?.result,
                    isLoading: message.isStreaming,
                    type:
                        message.error ||
                        message.payload?.error ||
                        message.payload?.result?.payload === "Something went wrong"
                            ? ChatBubbleType.error
                            : message.payload
                              ? ChatBubbleType.tivaData
                              : message.question
                                ? ChatBubbleType.answer
                                : ChatBubbleType.summary,
                    metadata: message.metadata
                };

                if (list.find(item => item.question?.id === message.id)) {
                    return list.map(item =>
                        item.question?.id === message.id
                            ? {
                                  ...item,
                                  answer: answer
                              }
                            : item
                    );
                } else {
                    list.push({ answer: answer });
                    return list;
                }
            }
            return prevMessages;
        });
    }, []);

    useEffect(() => {
        if (!currentAccessToken || !user || !chatId) return;

        const eventSource = listenAnswers(user.uid, chatId);

        eventSource.onopen = () => {
            console.log("~~~ event source opend ~~~");
        };

        eventSource.onmessage = ({ data }) => {
            const message: SseMessage = JSON.parse(data);
            handleAnswer(message);
        };

        eventSource.onerror = () => {
            console.log("~~~ error when listening to event source ~~~");
        };

        return () => {
            console.log("~~~ event source closed ~~~");
            eventSource.close();
        };
    }, [currentAccessToken, user, chatId, handleAnswer]);

    const opanTivaDataResult = (data: any) => {
        if (isAuth) {
            // setTivaDataPayload(data?.payload);
            // setTivaDataResultOpen(true);
            navigate(`${location.pathname}?tivaDataPayload=${JSON.stringify(data?.payload)}`);
        } else {
            showToast("You are not authorized in TIVA Data");
        }
    };

    const closeTivaDataResult = () => {
        navigate(location.pathname);
        // setTivaDataResultOpen(false);
        // setTivaDataPayload(undefined);
    };

    const handleSummarize = (summaryData: SummaryProps) => {
        if (user && chatId) {
            summarizeVideo(user.uid, chatId, summaryData).then(() => {
                availableScrollDownType.current = "smooth";
                setMessages(prevMessages => [...prevMessages, { answer: typingMessage() }]);
            });
        }
    };

    const handleLink = (metadata: any) => {
        if (metadata.playUrl) {
            setTimeout(() => {
                window.open(metadata.playUrl, "_blank");
            });
        } else if (metadata.s3Service && metadata.bucket && metadata.bucketFilePath) {
            getPresignedUrl({
                s3Service: metadata.s3Service,
                bucket: metadata.bucket,
                bucketFilePath: metadata.bucketFilePath
            }).then(response => {
                if (response) {
                    const pageParam =
                        metadata.source_type === SourceType.pdf && !!metadata.currentPage
                            ? `#page=${metadata.currentPage}`
                            : "";
                    setTimeout(() => {
                        window.open(`${response.data}${pageParam}`, "_blank");
                    });
                }
            });
        }
    };

    const getReviewValue = (feedback?: FeedbackProps) => {
        return feedback?.answerRate === "1"
            ? Feedback.like
            : feedback?.answerRate === "-1"
              ? Feedback.dislike
              : Feedback.noReview;
    };

    const handleReview = (id: number, feedback: Feedback) => {
        switch (feedback) {
            case Feedback.like:
                submitReview("1", id, "");
                break;
            case Feedback.noReview:
                submitReview("0", id, "");
                break;
            case Feedback.dislike:
                submitReview("-1", id, "");
                openReview(id);
                break;
        }
    };

    const submitReview = (value: string, messageId?: number, comment?: string) => {
        if (!messageId || !chatId || !user?.uid) return;

        feedback(user.uid, chatId, {
            messageId: messageId,
            questionContext: "0",
            answerRate: value,
            answerRateComment: comment ?? ""
        });

        updateReview(messageId, value, comment ?? "");
    };

    const updateReview = (id: number, value: string, comment: string) => {
        setMessages(prevMessages =>
            prevMessages.map(item =>
                item.answer?.id === id
                    ? {
                          ...item,
                          answer: {
                              ...item.answer,
                              feedback: {
                                  questionContext: "0",
                                  answerRate: value,
                                  answerRateComment: comment
                              }
                          }
                      }
                    : item
            )
        );
    };

    const openReview = (id: number) => {
        // const chat = document.getElementById("chat-messages");
        // const scroll = document.getElementById("chat-scroll");
        // if (scroll && chat && scroll.clientHeight === chat.clientHeight) {
        //     chat.style.minHeight = "200%";
        //     scroll.scrollTop = chat.clientHeight / 2 - 359 - 32;
        // }

        setActiveMessage(id);
        setTimeout(() => {
            scrollToReviewMessage();
            setTimeout(() => {
                updateReviewScroll();
            }, 500);
        });
    };

    const closeReview = () => {
        setActiveMessage(undefined);
        setReviewScroll(false);

        // const chat = document.getElementById("chat-messages");
        // if (chat) {
        //     chat.style.minHeight = "100%";
        // }
    };

    const updateReviewScroll = () => {
        const activeQuestion = document.getElementById(activeQuestionId);
        const activeAnswer = document.getElementById(activeAnswerId);
        const scroll = document.getElementById("chat-scroll");
        if (!!activeQuestion && !!activeAnswer && !!scroll) {
            const reviewHeight = activeQuestion.clientHeight + activeAnswer.clientHeight + 32;
            setReviewScrollTop(activeQuestion.offsetTop - 16);
            setReviewHeight(reviewHeight);
            setReviewScroll(scroll.clientHeight < reviewHeight);
        } else {
            setReviewScroll(false);
        }
    };

    const handleReviewScroll = (event: UIEvent<HTMLElement>) => {
        if (!activeMessage) {
            const {
                scrollTop,
                scrollHeight,
                clientHeight
            }: { scrollTop: number; scrollHeight: number; clientHeight: number } =
                event.currentTarget;
            setScrolledDown(scrollHeight - scrollTop - clientHeight <= 500);
        } else if (activeMessage && reviewScroll) {
            const { scrollTop, clientHeight }: { scrollTop: number; clientHeight: number } =
                event.currentTarget;
            if (scrollTop < reviewScrollTop) {
                event.currentTarget.scrollTop = reviewScrollTop;
            } else if (scrollTop > reviewScrollTop + reviewHeight - clientHeight) {
                event.currentTarget.scrollTop = reviewScrollTop + reviewHeight - clientHeight;
            }
        }
    };

    const getMessageStyle = (index: number) => {
        if (messages.length - 1 !== index || !chatScrollRef.current) return {};

        return {
            minHeight: `${chatScrollRef.current.clientHeight - footerHeight - (headerHeight + messageSpace)}px`
        };
    };

    const changeExpanded = (value: boolean, questionId?: number) => {
        if (!questionId) return;

        setMessages(prevMessages =>
            prevMessages.map(item =>
                item.question?.id === questionId
                    ? { ...item, question: { ...item.question, isExpanded: value } }
                    : item
            )
        );
    };

    return (
        <div className="chat-messages-content">
            <div
                ref={chatScrollRef}
                id="chat-scroll"
                className="chat-messages-scroll"
                style={{ overflowY: activeMessage && !reviewScroll ? "hidden" : "auto" }}
                onScroll={handleReviewScroll}
            >
                <div id="chat-messages" className="chat-messages">
                    {messages.length > 0 &&
                        messages.map((message, index) => (
                            <div
                                key={crypto.randomUUID()}
                                style={getMessageStyle(index)}
                                className="chat-bot-result-pair"
                            >
                                {message.question && (
                                    <div
                                        id={
                                            isActiveMessage(message.question)
                                                ? activeQuestionId
                                                : crypto.randomUUID()
                                        }
                                        className={`chat-bot-result--question ${isActiveMessage(message.question) ? "chat-bot-result--active" : ""}`}
                                        style={
                                            isMobileDevice()
                                                ? {
                                                      maxWidth: `${width - messagePadding * 2 - 30}px`
                                                  }
                                                : {}
                                        }
                                    >
                                        <ChatBubble
                                            id={message.question.id}
                                            message={message.question.text}
                                            isLoading={message.question.isLoading}
                                            type={message.question.type}
                                            isActive={isActiveMessage(message.question)}
                                            hideAvatars={true}
                                            isExpanded={message.question.isExpanded}
                                            setExpanded={(value: boolean) =>
                                                changeExpanded(value, message.question?.id)
                                            }
                                        />
                                    </div>
                                )}
                                {message.answer && (
                                    <div
                                        id={
                                            isActiveMessage(message.answer)
                                                ? activeAnswerId
                                                : crypto.randomUUID()
                                        }
                                        className={`chat-bot-result--answer ${isActiveMessage(message.answer) ? "chat-bot-result--active" : ""}`}
                                        style={
                                            isMobileDevice()
                                                ? {
                                                      maxWidth: `${width - messagePadding * 2 - 30}px`
                                                  }
                                                : {}
                                        }
                                    >
                                        <ChatBubble
                                            id={message.answer.id}
                                            message={message.answer.text}
                                            isLoading={message.answer.isLoading}
                                            type={message.answer.type}
                                            data={message.answer.data}
                                            metadata={message.answer.metadata}
                                            handleLink={handleLink}
                                            handleTivaDataResult={opanTivaDataResult}
                                            handleSummarize={handleSummarize}
                                            handleReview={handleReview}
                                            isActive={isActiveMessage(message.answer)}
                                            review={getReviewValue(message.answer.feedback)}
                                            showReview={true}
                                            showSources={true}
                                            showSummary={devMode}
                                            hideAvatars={true}
                                        />
                                        {isActiveMessage(message.answer) && (
                                            <div
                                                ref={reviewRef}
                                                className={
                                                    isMobileDevice()
                                                        ? "chat-review--mobile"
                                                        : "chat-review"
                                                }
                                            >
                                                <ChatMessageReview
                                                    closeAction={closeReview}
                                                    submitAction={(comment: string) =>
                                                        submitReview("-1", activeMessage, comment)
                                                    }
                                                />
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                    {messages.length > 0 && <div ref={messagesEndRef} />}
                    {messages.length === 0 && (
                        <SvgIcon
                            component={placeholder}
                            className="chat-placeholder"
                            inheritViewBox
                        />
                    )}
                </div>
            </div>
            <IconButton
                className={`chat-messages-scroll-button${isScrolledDown ? " chat-messages-scroll-button--hidden" : ""}`}
                onClick={() => scrollToBottom("smooth")}
            >
                <SvgIcon component={ArrowExpandIcon} inheritViewBox />
            </IconButton>
            <LoaderView show={isLoading} />
            <ChatMessageReviewBackdrop
                onClick={closeReview}
                open={!!activeMessage}
            ></ChatMessageReviewBackdrop>
            <Modal
                className="tiva-data-modal"
                open={tivaDataResultOpen}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <div className="tiva-data-container">
                    <TivaDataModalView handleClose={closeTivaDataResult} />
                </div>
            </Modal>
        </div>
    );
};
